/* ========================================
   PHLOOP - Theme JavaScript
   ======================================== */

(function() {
  'use strict';

  // --- Scroll Animations (IntersectionObserver) ---
  function initScrollAnimations() {
    const animatedElements = document.querySelectorAll('.fade-up, .fade-in, .slide-left, .slide-right');
    if (!animatedElements.length) return;

    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          // Stagger siblings that reveal together for a choreographed feel
          if (!reduce) {
            var sibs = entry.target.parentNode
              ? entry.target.parentNode.querySelectorAll(':scope > .fade-up')
              : [];
            var idx = Array.prototype.indexOf.call(sibs, entry.target);
            if (idx > 0) entry.target.style.transitionDelay = Math.min(idx * 90, 540) + 'ms';
          }
          entry.target.classList.add('visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -40px 0px' });

    animatedElements.forEach(function(el) { observer.observe(el); });
  }

  // --- Scroll Nav Dots ---
  function initScrollNavDots() {
    const dots = document.querySelectorAll('.scroll-nav__dot');
    const sections = [];
    dots.forEach(function(dot) {
      var target = document.querySelector(dot.getAttribute('data-target'));
      if (target) sections.push({ dot: dot, section: target });
    });

    if (!sections.length) return;

    // Click handler
    dots.forEach(function(dot) {
      dot.addEventListener('click', function() {
        var target = document.querySelector(this.getAttribute('data-target'));
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });

    // Scroll tracking via IntersectionObserver. Reading offsetTop/offsetHeight
    // inside a scroll handler forces a synchronous layout on every frame; the
    // observer gets the same answer off the main thread.
    if ('IntersectionObserver' in window) {
      var dotIo = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (!entry.isIntersecting) return;
          var match = sections.filter(function(i) { return i.section === entry.target; })[0];
          if (!match) return;
          dots.forEach(function(d) { d.classList.remove('active'); });
          match.dot.classList.add('active');
        });
      }, { rootMargin: '-33% 0px -60% 0px' });
      sections.forEach(function(item) { dotIo.observe(item.section); });
    }

    // Set initial active
    if (dots.length) dots[0].classList.add('active');
  }

  // --- Header Scroll Effect ---
  function initHeaderScroll() {
    var header = document.querySelector('.header');
    if (!header) return;

    var headerTicking = false;
    var headerScrolled = null;
    window.addEventListener('scroll', function() {
      if (headerTicking) return;
      headerTicking = true;
      requestAnimationFrame(function() {
        headerTicking = false;
        var next = window.scrollY > 10;
        if (next === headerScrolled) return;
        headerScrolled = next;
        header.classList.toggle('scrolled', next);
      });
    }, { passive: true });
  }

  // --- Header Active Nav Link ---
  function initActiveNavLinks() {
    var navLinks = document.querySelectorAll('.header__nav a[href^="#"]');
    if (!navLinks.length) return;

    // Was: a querySelector plus offsetTop/offsetHeight for every nav link on
    // every scroll event - the single worst forced-reflow source on the page.
    if ('IntersectionObserver' in window) {
      var linkFor = [];
      navLinks.forEach(function(link) {
        var target = document.querySelector(link.getAttribute('href'));
        if (target) linkFor.push({ link: link, target: target });
      });
      if (linkFor.length) {
        var navIo = new IntersectionObserver(function(entries) {
          entries.forEach(function(entry) {
            if (!entry.isIntersecting) return;
            var match = linkFor.filter(function(i) { return i.target === entry.target; })[0];
            if (!match) return;
            navLinks.forEach(function(l) { l.classList.remove('active'); });
            match.link.classList.add('active');
          });
        }, { rootMargin: '-100px 0px -60% 0px' });
        linkFor.forEach(function(i) { navIo.observe(i.target); });
      }
    }

    // Smooth scroll for nav links
    navLinks.forEach(function(link) {
      link.addEventListener('click', function(e) {
        e.preventDefault();
        var target = document.querySelector(this.getAttribute('href'));
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        // Close mobile nav if open
        var mobileNav = document.querySelector('.mobile-nav');
        if (mobileNav) mobileNav.classList.remove('open');
      });
    });
  }

  // --- Mobile Menu ---
  function initMobileMenu() {
    var toggle = document.querySelector('.header__menu-toggle');
    var mobileNav = document.querySelector('.mobile-nav');
    var closeBtn = document.querySelector('.mobile-nav__close');

    if (!toggle || !mobileNav) return;

    function closeNav() {
      mobileNav.classList.remove('open');
      mobileNav.setAttribute('aria-hidden', 'true');
      toggle.classList.remove('is-active');
      document.body.style.overflow = '';
    }
    function openNav() {
      mobileNav.classList.add('open');
      mobileNav.setAttribute('aria-hidden', 'false');
      toggle.classList.add('is-active');
      document.body.style.overflow = 'hidden';
    }

    toggle.addEventListener('click', function() {
      if (mobileNav.classList.contains('open')) closeNav();
      else openNav();
    });

    if (closeBtn) closeBtn.addEventListener('click', closeNav);

    // Close when tapping the dimmed backdrop (outside the panel)
    mobileNav.addEventListener('click', function(e) {
      if (e.target === mobileNav) closeNav();
    });

    // Close on Escape
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && mobileNav.classList.contains('open')) closeNav();
    });

    // Close on link click
    mobileNav.querySelectorAll('a').forEach(function(link) {
      link.addEventListener('click', closeNav);
    });
  }

  // --- FAQ Accordion ---
  function initFaqAccordion() {
    var faqItems = document.querySelectorAll('.faq__item');
    faqItems.forEach(function(item) {
      var question = item.querySelector('.faq__question');
      if (!question) return;
      question.addEventListener('click', function() {
        var wasOpen = item.classList.contains('open');
        // Close all
        faqItems.forEach(function(i) {
          i.classList.remove('open');
          var q = i.querySelector('.faq__question');
          if (q) q.setAttribute('aria-expanded', 'false');
        });
        // Toggle this one
        if (!wasOpen) {
          item.classList.add('open');
          question.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }

  // --- Product Utility Tabs ---
  function initUtilityTabs() {
    var tabs = document.querySelectorAll('.utility__tab');
    var panels = document.querySelectorAll('.utility__panel');
    if (!tabs.length) return;

    function activate(target) {
      tabs.forEach(function(t) { t.classList.toggle('active', t.getAttribute('data-tab') === target); });
      panels.forEach(function(p) { p.classList.toggle('active', p.getAttribute('data-panel') === target); });
    }

    // Auto-cycle the three uses while the section is on screen so visitors
    // who never click still see all of them. Any interaction stops it for good.
    var autoTimer = null;
    var userTookOver = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function stopAuto() {
      userTookOver = true;
      if (autoTimer) { clearInterval(autoTimer); autoTimer = null; }
    }
    function startAuto() {
      if (userTookOver || autoTimer) return;
      autoTimer = setInterval(function() {
        var current = document.querySelector('.utility__tab.active');
        var list = Array.prototype.slice.call(tabs);
        var next = list[(list.indexOf(current) + 1) % list.length];
        activate(next.getAttribute('data-tab'));
      }, 4500);
    }

    tabs.forEach(function(tab) {
      tab.addEventListener('click', function() {
        stopAuto();
        activate(this.getAttribute('data-tab'));
      });
    });

    var section = document.querySelector('.utility');
    if (section && 'IntersectionObserver' in window) {
      var io = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting) { startAuto(); }
          else if (autoTimer) { clearInterval(autoTimer); autoTimer = null; }
        });
      }, { threshold: 0.35 });
      io.observe(section);
    }
  }

  // --- Hero Image Gallery ---
  function initHeroGallery() {
    var thumbnails = document.querySelectorAll('.hero__thumbnail');
    var mainImage = document.querySelector('.hero__main-image');

    if (!thumbnails.length || !mainImage) return;

    thumbnails.forEach(function(thumb) {
      thumb.addEventListener('click', function() {
        thumbnails.forEach(function(t) { t.classList.remove('active'); });
        this.classList.add('active');
        var src = this.querySelector('img').getAttribute('data-full');
        if (src) {
          mainImage.setAttribute('src', src);
        }
      });
    });
  }

  // --- Cart Drawer ---
  function initCartDrawer() {
    var drawer = document.getElementById('cartDrawer');
    if (!drawer) return;

    // Open cart
    document.querySelectorAll('[data-cart-open]').forEach(function(btn) {
      btn.addEventListener('click', function(e) {
        e.preventDefault();
        drawer.classList.add('open');
        document.body.style.overflow = 'hidden';
      });
    });

    // Close cart
    document.querySelectorAll('[data-cart-close]').forEach(function(btn) {
      btn.addEventListener('click', function() {
        drawer.classList.remove('open');
        document.body.style.overflow = '';
      });
    });

    // Close on escape
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && drawer.classList.contains('open')) {
        drawer.classList.remove('open');
        document.body.style.overflow = '';
      }
    });
  }

  // --- Add to Cart (Shopify AJAX API) ---
  function initAddToCart() {
    document.querySelectorAll('[data-add-to-cart]').forEach(function(btn) {
      btn.addEventListener('click', function(e) {
        e.preventDefault();
        var variantId = this.getAttribute('data-variant-id');
        var quantity = parseInt(this.getAttribute('data-quantity') || '1', 10);

        if (!variantId) return;

        var originalText = this.textContent;
        this.textContent = 'Adding...';
        this.disabled = true;

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: parseInt(variantId, 10), quantity: quantity })
        })
        .then(function(res) { return res.json(); })
        .then(function(data) {
          btn.textContent = 'Added ✓';
          if (navigator.vibrate) navigator.vibrate(10);
          setTimeout(function() {
            btn.textContent = originalText;
            btn.disabled = false;
          }, 1500);
          // Refresh cart drawer
          refreshCart();
          // Open cart drawer
          var drawer = document.getElementById('cartDrawer');
          if (drawer) {
            drawer.classList.add('open');
            document.body.style.overflow = 'hidden';
          }
        })
        .catch(function(err) {
          console.error('Add to cart error:', err);
          btn.textContent = originalText;
          btn.disabled = false;
        });
      });
    });
  }

  // --- Fallback Add to Cart (Dynamic Catalog Fetch) ---
  function initFallbackAddToCart() {
    document.querySelectorAll('[data-fallback-add-to-cart]').forEach(function(btn) {
      btn.addEventListener('click', function(e) {
        e.preventDefault();
        var originalText = this.textContent;
        this.textContent = 'Adding...';
        this.disabled = true;

        fetch('/products.json?limit=1')
          .then(function(res) { return res.json(); })
          .then(function(data) {
            if (data.products && data.products.length > 0 && data.products[0].variants && data.products[0].variants.length > 0) {
              var variantId = data.products[0].variants[0].id;
              return fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: variantId, quantity: 1 })
              });
            } else {
              throw new Error('No product variants found in catalog');
            }
          })
          .then(function(res) { return res.json(); })
          .then(function() {
            btn.textContent = 'Added ✓';
            setTimeout(function() {
              btn.textContent = originalText;
              btn.disabled = false;
            }, 1500);
            refreshCart();
            var drawer = document.getElementById('cartDrawer');
            if (drawer) {
              drawer.classList.add('open');
              document.body.style.overflow = 'hidden';
            }
          })
          .catch(function(err) {
            console.error('Fallback add to cart error:', err);
            window.location.href = '/collections/all';
          });
      });
    });
  }

  // Money in the shop's format (INR, no decimals unless present)
  function cartMoney(cents) {
    var v = cents / 100;
    var s = (v % 1 === 0) ? v.toLocaleString('en-IN') : v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return '₹' + s;
  }
  function cartEsc(str) {
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
  var CART_SHIP_ICON = '<svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>';
  var CART_TRASH_ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>';

  function updateCartCount(cart) {
    var drawerCount = document.getElementById('cartDrawerCount');
    if (drawerCount) drawerCount.textContent = cart.item_count > 0 ? '(' + cart.item_count + ')' : '';
    var badge = document.querySelector('.header__cart-count');
    if (!badge) return;
    badge.textContent = cart.item_count;
    badge.style.display = cart.item_count > 0 ? 'flex' : 'none';
    badge.classList.remove('bump'); void badge.offsetWidth; badge.classList.add('bump');
  }

  // Per-line MRP (compare-at) from the map rendered by Liquid
  function lineMrp(item) {
    var map = window.PHLOOP_MRP || {};
    var unit = map[item.variant_id] || map[item.id] || 0;
    return unit > item.final_price ? unit * item.quantity : 0;
  }

  // Build a premium line item
  function cartItemHtml(item) {
    var title = cartEsc(item.product_title || item.title);
    var variant = (item.variant_title && item.variant_title !== 'Default Title') ? '<div class="cart-item__variant">' + cartEsc(item.variant_title) + '</div>' : '';
    var img = item.image ? '<a href="' + item.url + '"><img class="cart-item__image" src="' + item.image + '" alt="' + title + '" width="68" height="68" loading="lazy"></a>' : '<span class="cart-item__image"></span>';
    var mrp = lineMrp(item);
    var priceBlock = mrp
      ? '<span class="cart-item__prices"><s class="cart-item__mrp">' + cartMoney(mrp) + '</s><span class="cart-item__line" data-line>' + cartMoney(item.final_line_price) + '</span></span>'
      : '<span class="cart-item__line" data-line>' + cartMoney(item.final_line_price) + '</span>';
    return '' +
      '<div class="cart-item" data-key="' + item.key + '">' +
        img +
        '<div class="cart-item__details">' +
          '<div class="cart-item__title"><a href="' + item.url + '">' + title + '</a></div>' +
          variant +
          '<div class="cart-item__row">' +
            '<div class="cart-item__qty">' +
              '<button type="button" data-cart-dec aria-label="Decrease quantity">&minus;</button>' +
              '<span data-qty>' + item.quantity + '</span>' +
              '<button type="button" data-cart-inc aria-label="Increase quantity">+</button>' +
            '</div>' +
            '<button type="button" class="cart-item__trash" data-cart-remove aria-label="Remove item">' + CART_TRASH_ICON + '</button>' +
            priceBlock +
          '</div>' +
        '</div>' +
      '</div>';
  }

  // Total savings vs MRP across the cart
  function cartSavings(cart) {
    return cart.items.reduce(function (sum, item) {
      var mrp = lineMrp(item);
      return sum + (mrp ? mrp - item.final_line_price : 0);
    }, 0);
  }

  // Total + savings in the footer (called on render and on every change)
  function updateFooterTotals(cart) {
    var total = document.getElementById('cartTotal');
    if (total) total.textContent = cartMoney(cart.total_price);
    var savingsEl = document.getElementById('cartDrawerSavings');
    if (savingsEl) {
      var saved = cartSavings(cart);
      if (saved > 0) {
        var pct = Math.round(saved / (cart.total_price + saved) * 100);
        savingsEl.textContent = cartMoney(saved) + ' (' + pct + '% off)';
        savingsEl.style.display = 'flex';
      } else {
        savingsEl.style.display = 'none';
      }
    }
  }

  // Render the whole drawer from a cart object (single source, no extra fetch)
  function renderCart(cart) {
    var body = document.getElementById('cartDrawerBody');
    var footer = document.getElementById('cartDrawerFooter');
    updateCartCount(cart);
    if (!body) return;

    if (cart.item_count === 0) {
      body.innerHTML = '<p class="cart-empty-message">Your cart is empty - and your phone is still living dangerously.</p>';
      if (footer) footer.style.display = 'none';
      return;
    }
    body.innerHTML = cart.items.map(cartItemHtml).join('');
    if (footer) footer.style.display = 'block';
    updateFooterTotals(cart);
  }

  // Load / after add-to-cart: one fetch, then render
  function refreshCart() {
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function(res) { return res.json(); })
      .then(renderCart)
      .catch(function(err) { console.error('Cart refresh error:', err); });
  }

  // A quantity change is a SINGLE request - change.js returns the full cart,
  // so we update in place from that response instead of re-fetching. The row
  // is optimistically bumped and locked while the request is in flight.
  window.updateCartItem = function(key, quantity, row) {
    if (row) row.classList.add('is-busy');
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: Math.max(0, quantity) })
    })
    .then(function(res) { return res.json(); })
    .then(function(cart) {
      updateCartCount(cart);

      if (quantity <= 0) {
        if (row) row.remove();
        if (cart.item_count === 0) { renderCart(cart); return; }
        updateFooterTotals(cart);
        return;
      }
      var updated = cart.items.filter(function(it) { return it.key === key; })[0];
      if (updated && row) {
        row.querySelector('[data-qty]').textContent = updated.quantity;
        row.querySelector('[data-line]').textContent = cartMoney(updated.final_line_price);
        var mrpEl = row.querySelector('.cart-item__mrp');
        var mrp = lineMrp(updated);
        if (mrpEl && mrp) mrpEl.textContent = cartMoney(mrp);
        row.classList.remove('is-busy');
        updateFooterTotals(cart);
      } else {
        renderCart(cart);
      }
    })
    .catch(function(err) { console.error('Cart update error:', err); if (row) row.classList.remove('is-busy'); });
  };

  // Delegate qty +/- and remove from the (re-rendered) drawer body. Line item
  // keys are strings and must never be dropped into inline handlers.
  document.addEventListener('click', function(e) {
    if (!e.target.closest) return;
    var row = e.target.closest('.cart-item');
    if (!row) return;
    var key = row.getAttribute('data-key');
    var current = parseInt((row.querySelector('[data-qty]') || {}).textContent, 10) || 0;
    if (e.target.closest('[data-cart-inc]')) { window.updateCartItem(key, current + 1, row); }
    else if (e.target.closest('[data-cart-dec]')) { window.updateCartItem(key, current - 1, row); }
    else if (e.target.closest('[data-cart-remove]')) { window.updateCartItem(key, 0, row); }
  });

  // --- Cost of Drop Calculator ---
  function initDropCalculator() {
    var phoneSelect = document.getElementById('phoneModel');
    var resultEl = document.getElementById('repairCost');
    if (!phoneSelect || !resultEl) return;

    var costs = {
      'iphone-15-pro': '₹29,900',
      'iphone-15': '₹22,900',
      'iphone-14': '₹19,900',
      'samsung-s24': '₹24,500',
      'samsung-s23': '₹20,500',
      'pixel-8': '₹18,900',
      'oneplus-12': '₹16,900',
      'other': '₹15,000+'
    };

    phoneSelect.addEventListener('change', function() {
      var cost = costs[this.value] || '-';
      resultEl.textContent = cost;
    });
  }

  // --- Sticky Mobile Checkout Bar ---
  function initStickyMobileBar() {
    var stickyBar = document.getElementById('stickyMobileBar');
    if (!stickyBar) return;

    /* Cart mode renders only on the product page, where a real add-to-cart
       button exists with the shopper's chosen variant. Clicking it rather than
       posting our own cart/add keeps one source of truth for the variant,
       quantity and the drawer that opens afterwards. */
    var stickyBuy = stickyBar.querySelector('[data-sticky-buy]');
    if (stickyBuy) {
      stickyBuy.addEventListener('click', function () {
        var real = document.querySelector('[data-add-to-cart], [data-fallback-add-to-cart]');
        if (real) { real.click(); return; }
        /* Should not happen - cart mode implies the product page - but never
           leave the button inert if the markup ever changes. */
        var link = stickyBar.getAttribute('data-product-url');
        window.location.href = link || '/products/phloop-bracelet-phone-holder';
      });
    }

    // heroBtn may not exist on homepage (slider-only layout) - fall back to scroll threshold
    var heroBtn = document.querySelector('.hero__btn, [data-add-to-cart]');
    // hide the bar once the footer is in view so it never covers footer content
    // (and the redundant "buy" bar gets out of the way near the page-bottom CTA)
    var footer = document.querySelector('.footer');

    // Two getBoundingClientRect() reads per scroll event forced a layout on
    // every frame. Observers report the same two booleans for free.
    var heroPassed = !heroBtn;
    /* Content pages carry an inline product box near the top. While it is on
       screen or still ahead, the bar would repeat it, so the bar waits until
       the reader has scrolled past the box. */
    var ipcBox = document.querySelector('.ipc');
    var ipcPassed = !ipcBox;
    var footerVisible = false;
    var barShown = null;

    /* On the product page the bar is a buy control and should arrive as soon as
       the main button scrolls away. Everywhere else it is a suggestion, so make
       it wait until the reader has actually engaged rather than greeting them
       with a price the moment the page loads. */
    var isCartMode = stickyBar.getAttribute('data-sb-mode') === 'cart';
    var engaged = isCartMode;
    if (!engaged) {
      var depth = document.createElement('div');
      depth.setAttribute('aria-hidden', 'true');
      depth.style.cssText = 'position:absolute;top:30%;left:0;height:1px;width:1px;pointer-events:none;';
      document.body.appendChild(depth);
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (e) {
          if (e[0].isIntersecting) { engaged = true; syncBar(); }
        }, {}).observe(depth);
      } else {
        engaged = true;
      }
    }

    function syncBar() {
      var showBar = engaged && heroPassed && ipcPassed && !footerVisible && window.innerWidth <= 768;
      if (showBar === barShown) return;
      barShown = showBar;
      stickyBar.classList.toggle('visible', showBar);
      stickyBar.setAttribute('aria-hidden', showBar ? 'false' : 'true');
    }

    if ('IntersectionObserver' in window) {
      if (ipcBox) {
        new IntersectionObserver(function(entries) {
          ipcPassed = !entries[0].isIntersecting && entries[0].boundingClientRect.top < 0;
          syncBar();
        }, { threshold: 0 }).observe(ipcBox);
      }
      if (heroBtn) {
        new IntersectionObserver(function(entries) {
          heroPassed = !entries[0].isIntersecting && entries[0].boundingClientRect.top < 0;
          syncBar();
        }, { threshold: 0 }).observe(heroBtn);
      }
      if (footer) {
        new IntersectionObserver(function(entries) {
          footerVisible = entries[0].isIntersecting;
          syncBar();
        }, { threshold: 0 }).observe(footer);
      }
      window.addEventListener('resize', syncBar, { passive: true });
    } else {
      var barTicking = false;
      window.addEventListener('scroll', function() {
        if (barTicking) return;
        barTicking = true;
        requestAnimationFrame(function() {
          barTicking = false;
          heroPassed = window.scrollY > 400;
          ipcPassed = true;
          syncBar();
        });
      }, { passive: true });
    }
  }

  // --- Pause perpetual animations when off-screen ---
  // Marquees + the hero stamp keep looping when visible (as intended) but
  // pause once scrolled away - calmer peripheral vision + less CPU/battery.
  function initPauseOffscreen() {
    var els = document.querySelectorAll('.bftick__track, .hsolo-rot, .hsolo-india');
    if (!els.length || !('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        entry.target.classList.toggle('anim-paused', !entry.isIntersecting);
      });
    }, { rootMargin: '120px 0px' });
    els.forEach(function(el) { io.observe(el); });
  }

  // --- Stagger Animation Helper ---
  function staggerChildren(parentSelector, childSelector, delayMs) {
    var parent = document.querySelector(parentSelector);
    if (!parent) return;

    var observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          var children = entry.target.querySelectorAll(childSelector);
          children.forEach(function(child, i) {
            child.style.transitionDelay = (i * delayMs) + 'ms';
            child.classList.add('visible');
          });
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });

    observer.observe(parent);
  }

  // --- Init All ---
  function init() {
    initScrollAnimations();
    initScrollNavDots();
    initHeaderScroll();
    initActiveNavLinks();
    initMobileMenu();
    initFaqAccordion();
    initUtilityTabs();
    initHeroGallery();
    initCartDrawer();
    initAddToCart();
    initFallbackAddToCart();
    initDropCalculator();
    initStickyMobileBar();
    initPauseOffscreen();

    // Stagger animations
    staggerChildren('.how-works__steps', '.how-works__step', 150);
    staggerChildren('.moments__grid', '.moments__card', 100);
    staggerChildren('.materials__grid', '.materials__card', 150);
    staggerChildren('.specs__grid', '.specs__item', 100);
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
